import { NativeModules, Platform } from "react-native";

/**
 * iOS Family Controls app blocking (Screen Time authorization, FamilyActivityPicker, ManagedSettings).
 *
 * Example:
 *
 *   import {
 *     requestPermission,
 *     startAppSelection,
 *     blockApps,
 *     unblockApps,
 *     getCapabilities,
 *   } from "./platform/appBlockingBridge";
 *
 *   const caps = await getCapabilities();
 *   if (!caps.pickerSupported) { ... }
 *   await requestPermission();
 *   const pick = await startAppSelection();
 *   await blockApps(pick.selectionBase64);
 *   await unblockApps();
 */
const Native = NativeModules.AppBlockingModule;

function ensureIos() {
  if (Platform.OS !== "ios") {
    return false;
  }
  if (!Native) {
    return false;
  }
  return true;
}

export async function getCapabilities() {
  if (!ensureIos()) {
    return {
      nativeModule: false,
      osSupported: false,
      pickerSupported: false,
      authorized: false,
      authorizationStatus: "unsupported",
    };
  }
  try {
    return await Native.blockingCapabilities();
  } catch {
    return {
      nativeModule: true,
      osSupported: false,
      pickerSupported: false,
      authorized: false,
      authorizationStatus: "error",
    };
  }
}

/** @returns {Promise<{ supported: boolean, authorized?: boolean, status?: string, reason?: string }>} */
export async function requestPermission() {
  if (!ensureIos()) {
    return { supported: false, reason: "Not iOS or native module missing (add AppBlockingModule.swift to the Xcode project)." };
  }
  return Native.requestPermission();
}

/**
 * Presents FamilyActivityPicker. Resolves with selection payload or throws.
 * @returns {Promise<{ supported: boolean, selectionBase64: string, applications: number, categories: number, webDomains: number }>}
 */
export async function startAppSelection() {
  if (!ensureIos()) {
    throw new Error("App blocking is only available on iOS with the native module linked.");
  }
  const r = await Native.startAppSelection();
  if (r && r.supported === false) {
    throw new Error(r.reason || "Family Controls not supported on this OS build.");
  }
  return r;
}

/** @param {string} selectionBase64 plist base64 from startAppSelection */
export async function blockApps(selectionBase64) {
  if (!ensureIos()) {
    return false;
  }
  if (!selectionBase64) {
    throw new Error("selectionBase64 is required");
  }
  return Native.blockApps(selectionBase64);
}

export async function unblockApps() {
  if (!ensureIos()) {
    return false;
  }
  return Native.unblockApps();
}

export async function clearScheduledShields() {
  if (!ensureIos()) {
    return false;
  }
  if (!Native.clearScheduledShields) {
    return false;
  }
  return Native.clearScheduledShields();
}

/**
 * Registers a repeating DeviceActivity schedule. Requires a Device Activity Monitor extension
 * (see native-ios/DeviceActivityMonitor/GoalsTrackerDeviceActivityMonitor.swift).
 */
export async function startFocusSchedule({ startHour = 9, startMinute = 0, endHour = 17, endMinute = 0 } = {}) {
  if (!ensureIos()) {
    return { ok: false };
  }
  return Native.startFocusSchedule(startHour, startMinute, endHour, endMinute);
}

export async function stopFocusSchedule() {
  if (!ensureIos()) {
    return false;
  }
  return Native.stopFocusSchedule();
}
